import { Transformer } from '@lexical/markdown';
import { $createTextNode, ElementNode, LexicalNode, TextNode } from 'lexical';

import { unescapeInnerMarkdown } from '../../markdownEscaping';
import { HIGHLIGHT_COLORS, HighlightColor } from './highlightConstants';
import { $createHighlightNode, HighlightNode } from './HighlightNode';

const VALID_COLORS = Object.keys(HIGHLIGHT_COLORS).join('|');

// The color prefix (e.g. `yellow:`) is optional so we remain backward
// compatible with legacy `==text==` content produced by the built-in
// Lexical HIGHLIGHT transformer. When absent, we fall back to yellow.
const DEFAULT_COLOR: HighlightColor = 'yellow';

// Matches ==[color:]text== anywhere in a line (used during markdown import)
const IMPORT_REGEXP = new RegExp(`==(?:(${VALID_COLORS}):)?(.+?)==`);

// Matches ==[color:]text== at end of line (used during live typing)
const LIVE_REGEXP = new RegExp(`==(?:(${VALID_COLORS}):)?(.+?)==$`);

export const highlightTransformer: Transformer = {
  dependencies: [HighlightNode],
  export: (
    node: LexicalNode,
    exportChildren: (node: ElementNode) => string
  ) => {
    if (node instanceof HighlightNode) {
      const color = node.getColor();
      const childrenText = exportChildren(node);
      return `==${color}:${childrenText}==`;
    }
    return null;
  },
  importRegExp: IMPORT_REGEXP,
  regExp: LIVE_REGEXP,
  replace: (textNode: TextNode, match: RegExpMatchArray) => {
    const [, color, text] = match;
    if (!text) return;

    const highlightNode = $createHighlightNode(
      (color as HighlightColor | undefined) ?? DEFAULT_COLOR
    );
    // Unescape markdown characters so that inner formatting (e.g. **bold**)
    // is recognised by text-format transformers during recursive import.
    const textChild = $createTextNode(unescapeInnerMarkdown(text));
    textChild.setFormat(textNode.getFormat());
    highlightNode.append(textChild);
    textNode.replace(highlightNode);

    // Return the child so Lexical's markdown import recursively processes it
    // for text-format transforms (bold, italic, etc.) within the highlight.
    return textChild;
  },
  trigger: '=',
  type: 'text-match',
};
